import { useNavigate, useParams } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { OnboardingNavyShell } from '@/components/onboarding/NavyShell'
import { Avancement } from '@/pages/onboarding/MiseEnService'
import { ApercuApp } from '@/pages/onboarding/Apercus'
import { defaultLocale } from '@/lib/locales'
import { MAC_DMG_URL } from '@/lib/config'

const APP_STORE_URL = 'https://apps.apple.com/ca/app/callizy/id6755805657'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.callizy.softphone'
const QR_URL = 'https://callizy.ca/app'

/** Les badges officiels d'Apple et de Google, les mêmes que sur callizy.ca/app. */
const badgeAppStore = (fr: boolean) =>
  `https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/${fr ? 'fr-fr' : 'en-us'}?size=200x67&releaseDate=1700000000`
const badgePlay = (fr: boolean) =>
  fr
    ? 'https://play.google.com/intl/fr/badges/static/images/badges/fr_badge_web_generic.png'
    : 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'

const lienBadge = 'inline-flex items-center h-12 transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white rounded-lg'

export function OnboardingMobilePage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const goToDashboard = () => navigate(`/${locale}/dashboard`)
  const fr = locale !== 'en'

  return (
    <OnboardingNavyShell
      deconnexion={false} entete={<Avancement etape={7} />} panneau={<ApercuApp locale={locale} lienQr={QR_URL} />}>
      <main className="flex-grow flex items-center justify-center px-5 py-6 md:py-10">
        <div className="w-full max-w-[500px]">
          <h1 className="text-center sm:text-left text-[28px] md:text-[34px] font-semibold leading-[1.15] tracking-[-0.02em] text-balance mb-3">
            {t('onboarding.mobile.title')}
          </h1>
          <p className="text-center sm:text-left text-[15px] mb-8" style={{ color: '#8B9BB4' }}>
            {t('onboarding.mobile.subtitle')}
          </p>

          {/* iPhone, Android et Mac : le code à scanner est à droite, sur ordinateur. */}
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className={lienBadge}>
              <img src={badgeAppStore(fr)} alt={t('onboarding.mobile.altAppStore')} className="h-[44px] w-auto" />
            </a>
            {/* Le badge Google a une marge transparente : on le grandit pour aligner les deux. */}
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className={lienBadge}>
              <img src={badgePlay(fr)} alt={t('onboarding.mobile.altPlay')} className="h-[64px] w-auto -my-[10px] -mx-[8px]" />
            </a>
            <a
              href={MAC_DMG_URL}
              className="inline-flex items-center gap-2 h-[44px] px-4 bg-black rounded-lg border border-white/30 transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            >
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.1 2.48-1.34.03-1.77-.79-3.29-.79-1.53 0-1.99.77-3.26.82-1.31.05-2.31-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.24-1.99 1.1-3.15-1.02.04-2.25.68-2.97 1.53-.64.73-1.21 1.93-1.05 3.06 1.13.09 2.26-.67 2.92-1.44Z" />
              </svg>
              <span className="text-white text-[15px] font-medium">{t('onboarding.mobile.forMac')}</span>
            </a>
          </div>

          <div className="w-full mt-14 pt-6 border-t border-white/[0.06] flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            {/* Retour en bas à gauche, sur la ligne du bouton principal. */}
            <button
              type="button"
              onClick={() => navigate(`/${locale}/onboarding/team`)}
              className="h-11 sm:-ml-2 px-2 text-[14px] font-medium inline-flex items-center justify-center gap-1.5 rounded-lg hover:text-white transition-colors"
              style={{ color: '#8B9BB4' }}
            >
              <ArrowLeft className="w-4 h-4" />
              {t('onboarding.miseEnService.back')}
            </button>
            <button
              onClick={goToDashboard}
              className="w-full sm:w-auto h-11 px-7 rounded-xl text-[15px] font-semibold whitespace-nowrap transition-all hover:bg-white/90 active:scale-[0.98] flex items-center justify-center gap-2"
              style={{ backgroundColor: 'white', color: '#0A192F' }}
            >
              {t('onboarding.mobile.goToDashboard')}
            </button>
          </div>
        </div>
      </main>
    </OnboardingNavyShell>
  )
}
